// src/components/Call/VideoCall.jsx
import React, { useEffect, useRef, useState } from "react";
import {
  joinRoom,
  leaveRoom,
  getMedia,
  sendOffer,
  sendAnswer,
  onOffer,
  onAnswer,
  onCandidate,
  createPeerConnection,
} from "../../socket/signalingClient";

const VideoCall = ({ roomId, userId, onClose }) => {
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);

  const pcRef = useRef(null);
  const localStreamRef = useRef(null);

  const [connected, setConnected] = useState(false);

  // ----------------------------------------------------
  // INITIALIZE VIDEO CALL
  // ----------------------------------------------------
  useEffect(() => {
    initCall();
    return () => {
      if (pcRef.current) pcRef.current.close();
      leaveRoom(roomId, userId);
    };
  }, []);

  const initCall = async () => {
    // JOIN ROOM
    joinRoom(roomId, userId);

    // GET MEDIA
    const stream = await getMedia(true, true);
    if (!stream) {
      alert("Camera/Mic access failed!");
      return;
    }

    localStreamRef.current = stream;
    localVideoRef.current.srcObject = stream;

    // CREATE PEER
    pcRef.current = createPeerConnection(null, handleRemoteStream);

    // ADD TRACKS
    stream.getTracks().forEach((track) => {
      pcRef.current.addTrack(track, stream);
    });

    // SIGNALING HANDLERS
    setupSignalListeners();

    setConnected(true);
  };

  // ----------------------------------------------------
  // REMOTE STREAM HANDLER
  // ----------------------------------------------------
  const handleRemoteStream = (stream) => {
    remoteVideoRef.current.srcObject = stream;
  };

  // ----------------------------------------------------
  // SIGNALING EVENTS
  // ----------------------------------------------------
  const setupSignalListeners = () => {
    // OFFER RECEIVED
    onOffer(async ({ offer, from }) => {
      if (!pcRef.current) return;

      await pcRef.current.setRemoteDescription(offer);

      const answer = await pcRef.current.createAnswer();
      await pcRef.current.setLocalDescription(answer);

      sendAnswer({ answer, to: from });
    });

    // ANSWER RECEIVED
    onAnswer(async ({ answer }) => {
      if (!pcRef.current) return;
      await pcRef.current.setRemoteDescription(answer);
    });

    // ICE CANDIDATE RECEIVED
    onCandidate(async ({ candidate }) => {
      if (!pcRef.current) return;
      try {
        await pcRef.current.addIceCandidate(candidate);
      } catch (e) {
        console.error("Error adding ICE:", e);
      }
    });
  };

  // ----------------------------------------------------
  // CALL OTHER USER (INITIATE OFFER)
  // ----------------------------------------------------
  const startCall = async () => {
    if (!pcRef.current) return;

    const offer = await pcRef.current.createOffer();
    await pcRef.current.setLocalDescription(offer);

    // broadcast offer inside room
    sendOffer({ offer, to: "broadcast" });
  };

  // ----------------------------------------------------
  // UI
  // ----------------------------------------------------
  return (
    <div style={styles.container}>
      <h2 style={styles.title}>Video Call - Room {roomId}</h2>

      <div style={styles.videoContainer}>
        <video ref={localVideoRef} autoPlay muted playsInline style={styles.video} />
        <video ref={remoteVideoRef} autoPlay playsInline style={styles.video} />
      </div>

      <div style={styles.controls}>
        <button onClick={startCall} style={styles.btn}>
          Start Call
        </button>

        <button
          onClick={() => {
            if (pcRef.current) pcRef.current.close();
            leaveRoom(roomId, userId);
            onClose();
          }}
          style={{ ...styles.btn, background: "#c62828" }}
        >
          End Call
        </button>
      </div>
    </div>
  );
};

export default VideoCall;

const styles = {
  container: { padding: 20, textAlign: "center" },
  title: { marginBottom: 10, fontSize: 22, fontWeight: "bold" },
  videoContainer: {
    display: "flex",
    gap: 10,
    justifyContent: "center",
    marginBottom: 20,
  },
  video: {
    width: 300,
    height: 200,
    background: "#000",
    borderRadius: 10,
  },
  controls: { display: "flex", justifyContent: "center", gap: 20 },
  btn: {
    padding: "10px 20px",
    fontSize: 16,
    borderRadius: 8,
    border: "none",
    cursor: "pointer",
    background: "#1976d2",
    color: "white",
  },
};
